import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCompanies, createCompany, deleteCompany } from '../services/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { User, Building2, CreditCard, Bell, Shield, Plus, Trash2, LogOut, Mail, Eye, EyeOff, CheckCircle, Loader2, Monitor, Copy, Download, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import Sidebar from '../components/layout/Sidebar';
import { usePWA } from '../components/PWAInstallPrompt';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, logout, token } = useAuth();
  const { isInstalled, canInstall, install } = usePWA();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    legal_form: '',
    address: '',
    industry: '',
    vat_id: ''
  });

  // Email Settings State
  const [emailSettings, setEmailSettings] = useState({
    smtp_server: '',
    smtp_port: '587',
    smtp_email: '',
    smtp_password: '',
    sender_name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailConnected, setEmailConnected] = useState(false);

  const [notifications, setNotifications] = useState({
    email_daily: true,
    email_urgent: true,
    reminder_7days: true,
    reminder_2days: true
  });

  useEffect(() => {
    loadCompanies();
    loadEmailSettings();
  }, []);

  const loadEmailSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/email-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setEmailSettings(prev => ({
          ...prev,
          ...response.data,
          smtp_password: response.data.smtp_password ? '********' : ''
        }));
        setEmailConnected(!!response.data.smtp_email);
      }
    } catch (error) {
      console.log('No email settings found');
    }
  };

  const saveEmailSettings = async () => {
    if (!emailSettings.smtp_email || !emailSettings.smtp_password) {
      toast.error('Bitte E-Mail und Passwort eingeben');
      return;
    }
    
    setSavingEmail(true);
    try {
      await axios.post(`${API_URL}/api/user/email-settings`, emailSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('E-Mail-Einstellungen gespeichert!');
      setEmailConnected(true);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingEmail(false);
    }
  };

  const testEmailConnection = async () => {
    setTestingEmail(true);
    try {
      await axios.post(`${API_URL}/api/user/test-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test-E-Mail wurde gesendet!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-Mail-Test fehlgeschlagen');
    } finally {
      setTestingEmail(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.name) {
      toast.error('Bitte Firmennamen eingeben');
      return;
    }

    try {
      await createCompany(newCompany);
      toast.success('Unternehmen erstellt');
      setNewCompanyOpen(false);
      setNewCompany({ name: '', legal_form: '', address: '', industry: '', vat_id: '' });
      loadCompanies();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!confirm('Unternehmen wirklich löschen? Alle zugehörigen Dokumente bleiben erhalten.')) return;

    try {
      await deleteCompany(companyId);
      setCompanies(companies.filter(c => c.id !== companyId));
      toast.success('Unternehmen gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const tabs = [
    { id: 'profile', icon: User, label: 'Profil' },
    { id: 'desktop', icon: Monitor, label: 'Desktop App' },
    { id: 'email', icon: Mail, label: 'E-Mail' },
    { id: 'companies', icon: Building2, label: 'Unternehmen' },
    { id: 'notifications', icon: Bell, label: 'Benachrichtigungen' },
    { id: 'billing', icon: CreditCard, label: 'Abrechnung' },
    { id: 'security', icon: Shield, label: 'Sicherheit' },
  ];

  const industries = [
    'IT & Software',
    'Marketing & Agentur',
    'Beratung & Consulting',
    'Design & Kreativ',
    'Handwerk',
    'Gastronomie',
    'Einzelhandel',
    'Gesundheit',
    'Bildung',
    'Sonstiges'
  ];

  const legalForms = [
    'Einzelunternehmen',
    'GbR',
    'UG (haftungsbeschränkt)',
    'GmbH',
    'GmbH & Co. KG',
    'AG',
    'Freiberufler',
    'Sonstiges'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="settings-page">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob dark:opacity-20"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000 dark:opacity-20"></div>
      </div>

      <div className="flex relative z-10">
        <Sidebar />

        <main className="flex-1 min-h-screen">
          {/* Header */}
          <header className="glass-panel sticky top-0 z-40 border-b border-white/10">
            <div className="px-6 py-4">
              <h1 className="text-xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Einstellungen
              </h1>
            </div>
          </header>

          {/* Content */}
          <div className="p-6 md:p-8">
            <div className="max-w-4xl">
              {/* Tabs */}
              <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/25'
                        : 'glass-card text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card rounded-2xl p-6"
                >
                  <h2 className="text-lg font-semibold mb-6 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Dein Profil
                  </h2>
                  <div className="flex items-center gap-6 mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-indigo-500/25">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-lg text-foreground">{user?.name}</p>
                      <p className="text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-muted-foreground">Name</Label>
                      <Input value={user?.name || ''} disabled className="mt-2 glass-input" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">E-Mail</Label>
                      <Input value={user?.email || ''} disabled className="mt-2 glass-input" />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Desktop App Tab */}
              {activeTab === 'desktop' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  {/* PWA Install - Featured */}
                  <div className="glass-card rounded-2xl p-6 border-2 border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-purple-500/10">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                        {isInstalled ? (
                          <CheckCircle className="w-8 h-8 text-white" />
                        ) : (
                          <Download className="w-8 h-8 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h2 className="text-xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            JaySmart App
                          </h2>
                          {isInstalled ? (
                            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-medium">
                              Installiert
                            </span>
                          ) : (
                            <span className="text-xs bg-violet-500/20 text-violet-400 px-2 py-0.5 rounded-full font-medium animate-pulse">
                              Empfohlen
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {isInstalled 
                            ? 'Die App ist installiert und läuft auf deinem Gerät.' 
                            : 'Ein Klick - fertig! Keine Downloads, keine Installation.'}
                        </p>
                      </div>
                    </div>

                    {!isInstalled && canInstall && (
                      <Button
                        onClick={install}
                        className="w-full h-14 text-lg bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-lg shadow-violet-500/25"
                        data-testid="pwa-install-settings-btn"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Jetzt installieren
                      </Button>
                    )}

                    {!isInstalled && !canInstall && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                        <p className="text-sm text-amber-400">
                          <strong>Tipp:</strong> Öffne JaySmart in Chrome oder Edge, um die App zu installieren. 
                          Du siehst dann einen "Installieren" Button in der Adressleiste.
                        </p>
                      </div>
                    )}

                    {isInstalled && (
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                          <div>
                            <p className="text-sm font-medium text-emerald-400">App installiert</p>
                            <p className="text-xs text-emerald-400/70">Du kannst JaySmart jetzt aus deinem Dock/Startmenü öffnen.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Monitor className="w-4 h-4" />
                        <span>Desktop</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Smartphone className="w-4 h-4" />
                        <span>Mobile</span>
                      </div>
                      <span className="text-xs">•</span>
                      <span className="text-xs">Mac, Windows, Linux, iOS, Android</span>
                    </div>
                  </div>

                  {/* Download Section - Alternative */}
                  <div className="glass-card rounded-2xl p-6">
                    <h3 className="text-lg font-semibold mb-4 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Alternative Downloads
                    </h3>
                    
                    <div className="grid gap-4">
                      {/* Linux AppImage */}
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">🐧</span>
                              <span className="font-medium text-foreground">Linux AppImage</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Native Desktop-App für Linux</p>
                          </div>
                          <Button
                            onClick={() => window.open(`${API_URL}/api/download/linux-app`, '_blank')}
                            size="sm"
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            100 MB
                          </Button>
                        </div>
                      </div>

                      {/* Python Script */}
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">🐍</span>
                              <span className="font-medium text-foreground">Python Script</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Für Entwickler & Automatisierung</p>
                          </div>
                          <Button
                            onClick={() => window.open(`${API_URL}/api/download/sync-client`, '_blank')}
                            size="sm"
                            variant="outline"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            ZIP
                          </Button>
                        </div>
                      </div>

                      {/* Source Code */}
                      <div className="bg-gray-500/10 border border-gray-500/20 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">💻</span>
                              <span className="font-medium text-foreground">Quellcode (Electron)</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Baue DMG/EXE selbst: npm run build:mac</p>
                          </div>
                          <Button
                            onClick={() => window.open(`${API_URL}/api/download/desktop-app`, '_blank')}
                            size="sm"
                            variant="outline"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            ZIP
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* API Token Section */}
                  <div className="glass-card rounded-2xl p-6">
                    <h3 className="text-lg font-semibold mb-2 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      API Token
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Für Desktop-Clients und Automatisierung
                    </p>

                    <div className="relative">
                      <Input
                        type="text"
                        value={token || ''}
                        readOnly
                        className="font-mono text-xs pr-24 bg-black/30"
                      />
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(token);
                          toast.success('Token kopiert!');
                        }}
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 bg-gradient-to-r from-indigo-500 to-violet-500"
                        data-testid="copy-token-btn"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Kopieren
                      </Button>
                    </div>

                    <p className="text-xs text-muted-foreground mt-3">
                      Token ist 7 Tage gültig. Nach Ablauf einfach hier einen neuen kopieren.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Email Settings Tab */}
              {activeTab === 'email' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="glass-card rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          E-Mail-Verbindung
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          Verbinde deine E-Mail, um direkt aus JaySmart E-Mails zu versenden
                        </p>
                      </div>
                      {emailConnected && (
                        <div className="flex items-center gap-2 text-emerald-500 bg-emerald-500/10 px-3 py-1.5 rounded-full text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Verbunden
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-foreground">SMTP-Server</Label>
                          <Input
                            value={emailSettings.smtp_server}
                            onChange={(e) => setEmailSettings({ ...emailSettings, smtp_server: e.target.value })}
                            placeholder="smtp.office365.com"
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Port</Label>
                          <Input
                            value={emailSettings.smtp_port}
                            onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: e.target.value })}
                            placeholder="587"
                            className="mt-2"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-foreground">E-Mail-Adresse</Label>
                        <Input
                          type="email"
                          value={emailSettings.smtp_email}
                          onChange={(e) => setEmailSettings({ ...emailSettings, smtp_email: e.target.value })}
                          placeholder="deine@email.de"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label className="text-foreground">Passwort</Label>
                        <div className="relative mt-2">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={emailSettings.smtp_password}
                            onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                            placeholder="••••••••"
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <Label className="text-foreground">Absendername (optional)</Label>
                        <Input
                          value={emailSettings.sender_name}
                          onChange={(e) => setEmailSettings({ ...emailSettings, sender_name: e.target.value })}
                          placeholder="Max Mustermann"
                          className="mt-2"
                        />
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button
                          onClick={saveEmailSettings}
                          disabled={savingEmail}
                          className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600"
                        >
                          {savingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Speichern
                        </Button>
                        {emailConnected && (
                          <Button
                            onClick={testEmailConnection}
                            disabled={testingEmail}
                            variant="outline"
                          >
                            {testingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                            Test-E-Mail senden
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="glass-card rounded-2xl p-6">
                    <h3 className="font-medium text-foreground mb-4">Häufige SMTP-Server</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 rounded-xl bg-muted/50">
                        <p className="font-medium text-foreground">Microsoft 365 / GoDaddy</p>
                        <p className="text-muted-foreground">smtp.office365.com : 587</p>
                      </div>
                      <div className="p-3 rounded-xl bg-muted/50">
                        <p className="font-medium text-foreground">Gmail</p>
                        <p className="text-muted-foreground">smtp.gmail.com : 587</p>
                      </div>
                      <div className="p-3 rounded-xl bg-muted/50">
                        <p className="font-medium text-foreground">IONOS / 1&1</p>
                        <p className="text-muted-foreground">smtp.ionos.de : 587</p>
                      </div>
                      <div className="p-3 rounded-xl bg-muted/50">
                        <p className="font-medium text-foreground">Strato</p>
                        <p className="text-muted-foreground">smtp.strato.de : 465</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Companies Tab */}
              {activeTab === 'companies' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Deine Unternehmen
                    </h2>
                    <Dialog open={newCompanyOpen} onOpenChange={setNewCompanyOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 rounded-xl gap-2 shadow-lg shadow-indigo-500/25">
                          <Plus className="w-4 h-4" />
                          Neues Unternehmen
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="glass-panel border-white/20">
                        <DialogHeader>
                          <DialogTitle className="text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Neues Unternehmen anlegen
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div>
                            <Label className="text-foreground">Firmenname *</Label>
                            <Input
                              value={newCompany.name}
                              onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                              placeholder="Meine Firma GmbH"
                              className="mt-2 glass-input"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-foreground">Rechtsform</Label>
                              <Select
                                value={newCompany.legal_form}
                                onValueChange={(v) => setNewCompany({ ...newCompany, legal_form: v })}
                              >
                                <SelectTrigger className="mt-2 glass-input">
                                  <SelectValue placeholder="Auswählen..." />
                                </SelectTrigger>
                                <SelectContent className="glass-panel">
                                  {legalForms.map((form) => (
                                    <SelectItem key={form} value={form}>{form}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-foreground">Branche</Label>
                              <Select
                                value={newCompany.industry}
                                onValueChange={(v) => setNewCompany({ ...newCompany, industry: v })}
                              >
                                <SelectTrigger className="mt-2 glass-input">
                                  <SelectValue placeholder="Auswählen..." />
                                </SelectTrigger>
                                <SelectContent className="glass-panel">
                                  {industries.map((ind) => (
                                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label className="text-foreground">Adresse</Label>
                            <Input
                              value={newCompany.address}
                              onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
                              placeholder="Musterstraße 1, 12345 Berlin"
                              className="mt-2 glass-input"
                            />
                          </div>
                          <div>
                            <Label className="text-foreground">USt-ID (optional)</Label>
                            <Input
                              value={newCompany.vat_id}
                              onChange={(e) => setNewCompany({ ...newCompany, vat_id: e.target.value })}
                              placeholder="DE123456789"
                              className="mt-2 glass-input"
                            />
                          </div>
                          <Button onClick={handleCreateCompany} className="w-full bg-gradient-to-r from-indigo-500 to-violet-500 shadow-lg shadow-indigo-500/25">
                            Unternehmen erstellen
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {companies.length === 0 ? (
                    <div className="glass-card rounded-2xl p-8 text-center">
                      <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                      <p className="text-muted-foreground">Noch keine Unternehmen angelegt</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {companies.map((company) => (
                        <div
                          key={company.id}
                          className="glass-card rounded-2xl p-6 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500/20 to-violet-500/20 rounded-xl flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-medium text-foreground">{company.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {[company.legal_form, company.industry].filter(Boolean).join(' • ') || 'Keine Details'}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteCompany(company.id)}
                            className="p-2 hover:bg-red-500/20 rounded-lg text-muted-foreground hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card rounded-2xl p-6"
                >
                  <h2 className="text-lg font-semibold mb-6 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Benachrichtigungen
                  </h2>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Tägliche Zusammenfassung</p>
                        <p className="text-sm text-muted-foreground">Erhalte jeden Morgen eine E-Mail mit deinen Aufgaben</p>
                      </div>
                      <Switch
                        checked={notifications.email_daily}
                        onCheckedChange={(v) => setNotifications({ ...notifications, email_daily: v })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Dringende Benachrichtigungen</p>
                        <p className="text-sm text-muted-foreground">Sofortige E-Mail bei überfälligen Dokumenten</p>
                      </div>
                      <Switch
                        checked={notifications.email_urgent}
                        onCheckedChange={(v) => setNotifications({ ...notifications, email_urgent: v })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">7 Tage vorher erinnern</p>
                        <p className="text-sm text-muted-foreground">Erinnerung eine Woche vor Fälligkeit</p>
                      </div>
                      <Switch
                        checked={notifications.reminder_7days}
                        onCheckedChange={(v) => setNotifications({ ...notifications, reminder_7days: v })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">2 Tage vorher erinnern</p>
                        <p className="text-sm text-muted-foreground">Erinnerung zwei Tage vor Fälligkeit</p>
                      </div>
                      <Switch
                        checked={notifications.reminder_2days}
                        onCheckedChange={(v) => setNotifications({ ...notifications, reminder_2days: v })}
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Billing Tab */}
              {activeTab === 'billing' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card rounded-2xl p-6"
                >
                  <h2 className="text-lg font-semibold mb-6 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Abrechnung & Abo
                  </h2>
                  <div className="bg-gradient-to-br from-indigo-500 to-violet-500 rounded-2xl p-6 text-white mb-6 shadow-lg shadow-indigo-500/25">
                    <p className="text-sm opacity-80 mb-1">Aktueller Plan</p>
                    <p className="text-2xl font-bold mb-4">Pro</p>
                    <p className="text-sm opacity-80">9,30 EUR/Monat • Nächste Abrechnung: 01.02.2026</p>
                  </div>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Du nutzt derzeit den Pro-Plan mit unbegrenzten Dokumenten.
                    </p>
                    <Button variant="outline" className="rounded-xl glass-input">
                      Plan ändern
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="glass-card rounded-2xl p-6">
                    <h2 className="text-lg font-semibold mb-6 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Sicherheit
                    </h2>
                    <div className="space-y-4">
                      <Button variant="outline" className="rounded-xl w-full justify-start gap-2 glass-input">
                        <Shield className="w-4 h-4" />
                        Passwort ändern
                      </Button>
                    </div>
                  </div>

                  <div className="glass-card rounded-2xl p-6">
                    <h2 className="text-lg font-semibold mb-4 text-red-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Gefahrenzone
                    </h2>
                    <Button
                      variant="outline"
                      onClick={logout}
                      className="rounded-xl text-red-500 border-red-500/30 hover:bg-red-500/10 gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Abmelden
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
